import { TFunction } from "i18next";
import camelCase from "lodash/camelCase";

import { ActivityEntityKind } from "../../../model/activity/types/ActivityEntityKind.enum";
import { GetReportsResponseDto } from "../../../model/reports/dto/GetReportsResponseDto";
import { ReportKinds } from "../../../model/reports/types/ReportsKinds.enum";
import { CsvColumn, toCsv } from "../../../utils/csv";
import {
  formatReportPercent,
  getOriginLabel,
  getRangeLabel,
} from "../../../utils/Reports";

/**
 * The report holds several unrelated tables, and squeezing them into one sheet
 * would produce something no spreadsheet can filter or sort. Each dataset is
 * exported as its own file instead.
 *
 * The per-member activity detail is deliberately absent: it is capped server
 * side, so exporting it would hand over a silently partial view of the range.
 */
export enum ReportDataset {
  SUMMARY = "SUMMARY",
  MEMBERS = "MEMBERS",
  TREND = "TREND",
  SOURCES = "SOURCES",
  DEAL_VALUE = "DEAL_VALUE",
}

const METRIC_WINDOW_KEY: Record<string, string> = {
  [ReportKinds.ENTRY]: "reports.metric.createdInRange",
  [ReportKinds.ENTRY_LEAD]: "reports.metric.createdInRange",
  [ReportKinds.ENTRY_CONTACT]: "reports.metric.createdInRange",
  [ReportKinds.ENTRY_JUNK]: "reports.metric.createdInRange",
  [ReportKinds.DEAL]: "reports.metric.createdInRange",
  [ReportKinds.DEAL_WON]: "reports.metric.closedInRange",
  [ReportKinds.DEAL_LOST]: "reports.metric.closedInRange",
  [ReportKinds.DEAL_TO_COMPETITION]: "reports.metric.closedInRange",
  [ReportKinds.TASK]: "reports.metric.createdInRange",
  [ReportKinds.TASK_PENDING]: "reports.metric.openNow",
  [ReportKinds.TASK_COMPLETED]: "reports.metric.completedInRange",
};

type SummaryRow = {
  metric: string;
  window: string;
  value: number;
  previous: number | null;
};

const buildSummary = (
  report: GetReportsResponseDto,
  t: TFunction,
): { columns: CsvColumn<SummaryRow>[]; rows: SummaryRow[] } => {
  const rows: SummaryRow[] = Object.keys(METRIC_WINDOW_KEY).map((key) => ({
    metric: t(`reports.${camelCase(key)}.title`),
    window: t(METRIC_WINDOW_KEY[key]),
    value: report.metrics?.[key as ReportKinds] ?? 0,
    previous: report.previousMetrics?.[key as ReportKinds] ?? null,
  }));

  // A standing figure rather than a windowed one, so it has no previous.
  rows.push({
    metric: t("reports.taskOverdue.title"),
    window: t("reports.metric.overdueNow"),
    value: report.tasksOverdue ?? 0,
    previous: null,
  });

  if (report.rates?.dealWin !== null && report.rates?.dealWin !== undefined) {
    rows.push({
      metric: t("reports.rate.dealWin"),
      window: t("reports.metric.closedInRange"),
      // Same rounding `formatReportPercent` uses on screen (hundredths, not
      // a bare integer) so the export never disagrees with the tile it came
      // from over something as basic as 33 vs 33.33.
      value: Number(formatReportPercent(report.rates.dealWin)),
      previous: null,
    });
  }

  return {
    rows,
    columns: [
      { header: t("common.name"), value: (r) => r.metric },
      { header: t("reports.export.column.window"), value: (r) => r.window },
      { header: t("reports.export.column.value"), value: (r) => r.value },
      {
        header: t("reports.export.column.previous"),
        value: (r) => r.previous ?? "",
      },
      {
        header: t("reports.export.column.change"),
        value: (r) => (r.previous === null ? "" : r.value - r.previous),
      },
    ],
  };
};

type Built = { filename: string; content: string };

export const buildReportCsv = (
  dataset: ReportDataset,
  report: GetReportsResponseDto,
  t: TFunction,
): Built | null => {
  // The filename carries the window, so files pulled on different days do not
  // overwrite each other in the downloads folder.
  const windowLabel = report.scope?.length
    ? `${report.scope[0]}_${report.scope[1]}`
    : (report.range || "").toLowerCase();
  const name = (part: string) =>
    `report-${part}${windowLabel ? `-${windowLabel}` : ""}.csv`;

  if (dataset === ReportDataset.SUMMARY) {
    const { columns, rows } = buildSummary(report, t);
    return { filename: name("summary"), content: toCsv(columns, rows) };
  }

  if (dataset === ReportDataset.MEMBERS) {
    const rows = report.metricsByUser || [];

    if (!rows.length) {
      return null;
    }

    return {
      filename: name("members"),
      content: toCsv(
        [
          { header: t("common.members"), value: (r) => r.name },
          {
            header: t("common.entriesConvertedByUser"),
            value: (r) => r.entriesConverted,
          },
          {
            header: t("common.tasksClosedByUsers"),
            value: (r) => r.tasksDone,
          },
          {
            header: t("common.dealsClosedByUser"),
            value: (r) => r.dealsClosed,
          },
        ],
        rows,
      ),
    };
  }

  if (dataset === ReportDataset.TREND) {
    const rows = report.trend || [];

    if (!rows.length) {
      return null;
    }

    return {
      filename: name("activity"),
      content: toCsv(
        [
          { header: t("common.date"), value: (r) => r.day },
          {
            header: t("common.entries"),
            value: (r) => r[ActivityEntityKind.ENTRY] ?? 0,
          },
          {
            header: t("common.deals"),
            value: (r) => r[ActivityEntityKind.KANBAN_CARD] ?? 0,
          },
          {
            header: t("common.tasks"),
            value: (r) => r[ActivityEntityKind.TASK] ?? 0,
          },
          {
            header: t("common.modules"),
            value: (r) => r[ActivityEntityKind.MODULE] ?? 0,
          },
          {
            header: t("common.forms"),
            value: (r) => r[ActivityEntityKind.FORM] ?? 0,
          },
        ],
        rows,
      ),
    };
  }

  if (dataset === ReportDataset.SOURCES) {
    const rows = report.entriesByOrigin || [];

    if (!rows.length) {
      return null;
    }

    return {
      filename: name("sources"),
      content: toCsv(
        [
          {
            header: t("reports.origin.title"),
            value: (r) => getOriginLabel(r.origin, t),
          },
          { header: t("common.entries"), value: (r) => r.count },
        ],
        rows,
      ),
    };
  }

  const rows = report.dealValue || [];

  if (!rows.length) {
    return null;
  }

  return {
    filename: name("deal-value"),
    content: toCsv(
      [
        { header: t("common.currency"), value: (r) => r.currency },
        { header: t("reports.dealValue.created"), value: (r) => r.created },
        { header: t("reports.dealValue.won"), value: (r) => r.won },
        { header: t("reports.dealValue.lost"), value: (r) => r.lost },
        {
          header: t("reports.dealToCompetition.title"),
          value: (r) => r.toCompetition,
        },
      ],
      rows,
    ),
  };
};
